<template>
  <section class="full-height">
    <div>
      <span class="display-block mb-20">
        <title class="display-inline-block body1">1. Download the Kubernetes agent config per cluster</title>
      </span>
      <el-select
        class="display-block mb-20"
        v-model="selectedManageCluster"
        value-key="uuid"
        placeholder="Please select discovered cluster"
      >
        <el-option
          v-for="(cluster, idx) in clusters"
          :key="idx"
          :label="`${cluster.uniqueId}:${cluster.region}:${cluster.name}`"
          :value="cluster"
        ></el-option>
      </el-select>
      <el-button class="display-block mt-20" size="medium" round @click="updateIntegrationSettings"> Submit </el-button>
      <el-divider />
      <div class="header-carousal-2">Enter the Kubernetes settings if it is not found above</div>
      <el-select v-model="kubernetesSelection" value-key="id" placeholder="Select Cloud" class="mt-10 dropdown-border">
        <el-option v-for="(item, idx) in ['AWS', 'Azure', 'GCP']" :key="idx" :label="item" :value="item"> </el-option>
      </el-select>
      <div class="box flex">
        <el-form :v-model="kubernetesForm" label-width="240px" label-position="left" class="full-width">
          <el-row :type="'flex'" :gutter="8" class="mt-20 mb-20">
            <el-col :span="8" class="conf-block">
              <el-form-item label="Account Nickname"></el-form-item>
              <el-input
                v-model="kubernetesFormField.nickName"
                placeholder="Enter a nickname for your connection"
                @keydown.native.space.prevent
              ></el-input>
            </el-col>

            <el-col :span="8" class="conf-block" v-if="kubernetesSelection === 'Azure'">
              <el-form-item label="Subscription ID"></el-form-item>
              <el-input v-model="kubernetesFormField.roleArn" placeholder="Enter your Subscription ID"></el-input>
            </el-col>
            <el-col :span="8" class="conf-block" v-else-if="kubernetesSelection === 'GCP'">
              <el-form-item label="Project Name"></el-form-item>
              <el-input v-model="kubernetesFormField.roleArn" placeholder="Enter your Project Name"></el-input>
            </el-col>
            <el-col :span="8" class="conf-block" v-else>
              <el-form-item label="Role ARN"></el-form-item>
              <el-input v-model="kubernetesFormField.roleArn" placeholder="Enter your role ARN"></el-input>
            </el-col>

            <el-col :span="8" class="conf-block">
              <el-form-item label="Region"></el-form-item>
              <el-input v-model="kubernetesFormField.region" placeholder="Enter the Region"></el-input>
            </el-col>

            <el-col :span="8" class="conf-block">
              <el-form-item label="Cluster"></el-form-item>
              <el-input v-model="kubernetesFormField.cluster" placeholder="Enter the Cluster"></el-input>
            </el-col>
          </el-row>
          <el-button class="display-block mt-20" size="medium" round @click="submitIntegrationSettings">
            Submit
          </el-button>
        </el-form>
      </div>
    </div>
    <div class="mt-20 mb-20">
      <span>
        <title class="display-block body1">2. Install the Confluera Kubernetes agent per cluster using helm</title>
      </span>
      <pre>{{
        `# Add confluera helm repo
          helm repo add confluera_chart_museum https://charts.confluera.com\n
        # Install confluera k8s agent
          helm install confluera-k8s-agent confluera_chart_museum/confluera-agent -f &lt;path to yml generated in step 1&gt;`
      }}</pre>
    </div>
    <div class="mt-20 mb-20">
      <span>
        <title class="display-block body1">3. Verify kubernetes cluster discovery status below</title>
      </span>
    </div>
    <div class="mt-20 mb-20">
      <span>
        <title class="display-block body1">
          4. View discovered containers in <router-link class="display-inline-block " :to="{ name: 'containers' }">
          <a>Assets --> Containers</a> </router-link> view
        </title>
      </span>
    </div>
    <title class="display-block body1 mt-20 mb-10">Managed Clusters</title>
    <action-table-element
      class="integration-account-table"
      v-if="kubernetesAccountsReqStatus === 'saved'"
      ref="integrationTable"
      :actions="tableActions"
      :columns="visibleHostsColumns"
      :table-data="kubernetesAccounts"
      :height-offset="200"
    ></action-table-element>
  </section>
</template>

<script>
import { mapGetters } from 'vuex'
import { KUBERNETES_DISCOVERY_CLUSTERS_REQ } from '@/store/modules/agents/kubernetes-discovery.type'
import {
  KUBERNETES_INTEGRATION_CONFIG_CREATE_REQUEST,
  KUBERNETES_HELMCHART_CONFIG_REQUEST
} from '@/store/modules/integrations/kubernetes-integration.type'

import {
  KUBERNETES_ACCOUNTS_LIST_REQUEST,
  DELETE_CLOUD_ACCOUNTS_REQ,
  PAUSE_INTEGRATION_ACCOUNTS_REQ
} from '@/store/modules/integration_accounts/integration-accounts.type'

import OsInstallInstructions from '@/components/os-template/OsInstallInstructions'
import ActionTableElement from '@/components/table/ActionTableElement.vue'

import { kubernetesAccountsMetaInfo } from '@/column-header/integration_accounts/kubernetes-accounts.meta'

export default {
  name: 'KubernetesInstructions',
  extends: OsInstallInstructions,
  data() {
    return {
      hostsColumns: kubernetesAccountsMetaInfo.meta.columns,
      visibleHostsColumns: kubernetesAccountsMetaInfo.meta.columns,
      clusters: [],
      selectedManageCluster: undefined,
      integrationWizardForm: null,
      kubernetesSelection: 'AWS',
      kubernetesForm: null,
      kubernetesFormField: {
        nickName: '',
        region: '',
        cluster: '',
        roleArn: ''
      },
      tableActions: [
        {
          name: 'download',
          icon: 'c-icon material-icons',
          iconSubject: 'download',
          tooltip: 'Download Config YAML',
          rbac: {
            subject: 'rules',
            operation: 'write'
          },
          actionHandler: selection => {
            let vm = this
            this.$confirm(
              `This will download the yaml configurations for ${selection.row.nickName}. Continue?`,
              'Download Configurations',
              {
                confirmButtonText: 'OK',
                cancelButtonText: 'Cancel',
                callback: function (action) {
                  if (action === 'confirm') {
                    vm.getHelmchartConfig(selection.row)
                  } else if (action === 'cancel') {
                    vm.$message({
                      type: 'info',
                      message: `Operation canceled`
                    })
                  }
                }
              }
            )
          }
        },
        {
          name: 'delete',
          icon: 'c-icon el-icon-delete',
          tooltip: 'Delete Integration Account',
          rbac: {
            subject: 'rules',
            operation: 'write'
          },
          actionHandler: selection => {
            let vm = this
            this.$confirm(
              `<pre># Delete the confluera-k8s-agent from the kubernetes cluster
helm delete confluera-k8s-agent </pre> 

              <span>This will delete ${selection.row.nickName}. Continue?</span>`,
              'Delete cloud account',
              {
                dangerouslyUseHTMLString: true,
                customClass: 'k8-del-confirm',
                confirmButtonText: 'OK',
                cancelButtonText: 'Cancel',
                callback: function (action) {
                  if (action === 'confirm') {
                    vm.deleteAccount(selection.row)
                  } else if (action === 'cancel') {
                    vm.$message({
                      type: 'info',
                      message: `Operation canceled`
                    })
                  }
                }
              }
            )
          }
        },
        {
          name: 'pause',
          icon: 'c-icon far',
          tooltip: 'Enable/Pause Integration Account',
          rbac: {
            subject: 'rules',
            operation: 'write'
          },
          actionHandler: selection => {
            let vm = this
            let pause = selection.row.enabled ? 'Pause' : 'Enable'
            this.$confirm(
              `This will ${pause.toLowerCase()} ${selection.row.nickName}. Continue?`,
              `${pause} cloud account`,
              {
                confirmButtonText: 'OK',
                cancelButtonText: 'Cancel',
                callback: function (action) {
                  if (action === 'confirm') {
                    vm.pauseAccount(selection.row)
                  } else if (action === 'cancel') {
                    vm.$message({
                      type: 'info',
                      message: `Operation canceled`
                    })
                  }
                }
              }
            )
          }
        }
      ]
    }
  },
  methods: {
    getHelmchartConfig(row) {
      this.$store
        .dispatch(KUBERNETES_HELMCHART_CONFIG_REQUEST, {
          uniqueId: row.uniqueId
        })
        .then(resp => {})
    },
    submitIntegrationSettings() {
      if (
        !this.kubernetesFormField.nickName ||
        !this.kubernetesFormField.region ||
        !this.kubernetesFormField.cluster ||
        !this.kubernetesFormField.roleArn
      ) {
        this.$message({
          type: 'error',
          message: 'Please populate all fields.'
        })
        return false
      }
      this.kubernetesFormField.roleArn = this.kubernetesFormField.roleArn.trim()
      if (this.kubernetesSelection === 'Azure') {
        this.kubernetesFormField.roleArn = `${this.kubernetesFormField.roleArn}-AZURE`
      } else if (this.kubernetesSelection === 'GCP') {
        this.kubernetesFormField.roleArn = `${this.kubernetesFormField.roleArn}-GCP`
      }
      this.$store
        .dispatch(KUBERNETES_INTEGRATION_CONFIG_CREATE_REQUEST, {
          nickName: this.kubernetesFormField.nickName.trim(),
          region: this.kubernetesFormField.region.trim(),
          cluster: this.kubernetesFormField.cluster.trim(),
          roleArn: this.kubernetesFormField.roleArn
        })
        .then(resp => {
          this.kubernetesFormField.nickName = ''
          this.kubernetesFormField.region = ''
          this.kubernetesFormField.cluster = ''
          this.kubernetesFormField.roleArn = ''
          setTimeout(() => {
            this.refreshTableData({
              activePage: this.kubernetesAccounts.currentPage,
              searchTags: this.kubernetesAccounts.searchTags
            })
          }, 500)
        })
    },
    updateIntegrationSettings() {
      if (!this.selectedManageCluster || !this.selectedManageCluster.name) {
        this.$message({
          type: 'error',
          message: 'Please select a kubernetes configuration.'
        })
        return false
      }
      this.$store
        .dispatch(KUBERNETES_INTEGRATION_CONFIG_CREATE_REQUEST, {
          nickName: `Confluera Discovered ${this.selectedManageCluster.name}`,
          region: this.selectedManageCluster.region,
          cluster: this.selectedManageCluster.name,
          roleArn: this.selectedManageCluster.uniqueId
        })
        .then(resp => {
          setTimeout(() => {
            this.refreshTableData({
              activePage: this.kubernetesAccounts.currentPage,
              searchTags: this.kubernetesAccounts.searchTags
            })
          }, 500)
        })
    },
    callApi(tableMetaData) {
      Promise.all(
        [this.$store.dispatch(KUBERNETES_DISCOVERY_CLUSTERS_REQ)],
        this.$store.dispatch(KUBERNETES_ACCOUNTS_LIST_REQUEST)
      ).then(results => {
        this.clusters = results[0]
        this.setTableMetaData(tableMetaData)
      })
    },
    setTableMetaData(tableMetaData) {
      this.kubernetesAccounts.searchTags = tableMetaData.searchTags
      this.kubernetesAccounts.currentPage = tableMetaData.activePage
    },
    refreshTableData(tableMetaData) {
      this.callApi(tableMetaData)
    },
    deleteAccount(row) {
      this.$store.dispatch(DELETE_CLOUD_ACCOUNTS_REQ, row.key).then(agents => {
        setTimeout(() => {
          this.$message({
            type: 'success',
            message: `Kubernetes cluster ${row.nickName} was successfully removed.`
          })
          this.$store.dispatch(KUBERNETES_ACCOUNTS_LIST_REQUEST).then(integrations => {
            this.refreshTableData({
              activePage: this.kubernetesAccounts.currentPage,
              searchTags: this.kubernetesAccounts.searchTags
            })
          })
        }, 500)
      })
    },
    pauseAccount(row) {
      this.$store
        .dispatch(PAUSE_INTEGRATION_ACCOUNTS_REQ, {
          integrationId: row.key,
          action: row.enabled ? 'disable' : 'enable'
        })
        .then(agents => {
          setTimeout(() => {
            this.$store.dispatch(KUBERNETES_ACCOUNTS_LIST_REQUEST).then(integrations => {
              this.refreshTableData({
                activePage: this.kubernetesAccounts.currentPage,
                searchTags: this.kubernetesAccounts.searchTags
              })
            })
          }, 500)
        })
    }
  },
  created() {
    let tableMetaData = {
      activePage: 1,
      searchTags: []
    }

    this.callApi(tableMetaData)
  },
  computed: {
    ...mapGetters(['kubernetesAccountsReqStatus', 'kubernetesAccounts', 'kubernetesAccountsSummary'])
  },
  components: {
    ActionTableElement
  }
}
</script>

<style scoped>
@import 'assets/styles/_variables.css';
@import 'assets/styles/text.css';

title {
  color: var(--denim-700) !important;
  font-weight: bold !important;
}

pre {
  white-space: pre-line;
}

.install-button {
  vertical-align: middle;
}

.download-instr {
  line-height: 28px;
  padding: 4px;
  color: var(--denim-900);
  background-color: var(--denim-50);
  border: 1px solid var(--denim-900);

  &.complete {
    color: var(--shamrock-600);
    background-color: var(--shamrock-50);
    border: 1px solid var(--shamrock-600);
  }

  .done {
    vertical-align: text-bottom;
    margin-left: 25px;
  }
}

img {
  display: block;
  border: 1px solid var(--border-color);
}

/* .kubernetes-instructions {
  height: calc(100vh - 180px);
} */
</style>

<style>
.k8-del-confirm {
  width: 575px;
}
</style>



// WEBPACK FOOTER //
// src/components/os-template/KubernetesInstructions.vue